import { useCallback, useEffect, useRef, useState } from 'react'
import { SwiperRef } from 'swiper/react'

export const useClippedSlides = (swiperRef: React.RefObject<SwiperRef>) => {
  const swiperElRef = useRef<HTMLElement | null>(null)
  const [initialized, setInitialized] = useState(false)
  const [clippedIndexes, setClippedIndexes] = useState<number[]>([])

  const updateClippedSlideClasses = useCallback(() => {
    const swiper = swiperRef.current?.swiper
    if (!swiper) return

    const swiperEl = swiperElRef.current
    if (!swiperEl) return
    const viewportWidth = window.innerWidth

    const isSlideClipped = (slide: HTMLElement) => {
      const slideRect = slide.getBoundingClientRect()

      return slideRect.left < 0 || slideRect.right > viewportWidth
    }

    const clippedSlides: number[] = []

    swiper.slides.forEach((slide: HTMLElement, index: number) => {
      if (isSlideClipped(slide)) {
        clippedSlides.push(index)
      }
    })

    swiper.slides.forEach((slide, index) => {
      if (clippedSlides.includes(index)) {
        slide.classList.add('clipped-slide')
      } else {
        slide.classList.remove('clipped-slide')
      }
    })

    setClippedIndexes(clippedSlides)
  }, [swiperRef])

  useEffect(() => {
    const swiper = swiperRef.current?.swiper
    if (!swiper) return

    swiperElRef.current = swiper.el as HTMLElement
    updateClippedSlideClasses()
    setInitialized(true)

    swiper.on('slideChange', updateClippedSlideClasses)
    swiper.on('slideChangeTransitionEnd', updateClippedSlideClasses)
    window.addEventListener('resize', updateClippedSlideClasses)

    return () => {
      swiper.off('slideChange', updateClippedSlideClasses)
      swiper.off('slideChangeTransitionEnd', updateClippedSlideClasses)
      window.removeEventListener('resize', updateClippedSlideClasses)
    }
  }, [swiperRef, updateClippedSlideClasses])

  useEffect(() => {
    if (initialized) {
      const swiper = swiperRef.current?.swiper
      if (swiper) {
        swiper.update()
        updateClippedSlideClasses()
      }
    }
  }, [initialized, swiperRef, updateClippedSlideClasses])

  return clippedIndexes
}
